import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit,
  Power,
  PowerOff,
  Check,
  X,
  Loader2,
  Trash2,
} from 'lucide-react';

import { Button } from '../components/ui';
import { Breadcrumb } from '../components/Breadcrumb';
import { ErrorBanner, SuccessBanner } from '../components/tools';
import { apiService } from '../services/apiService';
import { Printer } from '../services/types';

interface PrinterFormState {
  name: string;
  ip: string;
  port: string;
}

const emptyForm: PrinterFormState = { name: '', ip: '', port: '9100' };

// Match DataTable's th/td classes so this page reads like other tables
// in the app. We can't use DataTable directly because the rows toggle
// into inline-edit mode, which DataTable doesn't model.
const HEADER_CELL_CLASS =
  'px-4 py-3 text-left font-semibold text-gray-700 text-sm';
const BODY_CELL_CLASS = 'px-4 py-3 text-gray-900 text-sm';

export const PrinterAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<PrinterFormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PrinterFormState>(emptyForm);

  const loadPrinters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const list = await apiService.listPrinters();
      setPrinters(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPrinters();
  }, []);

  const handleCreate = async () => {
    setError(null);
    setSuccess(null);
    try {
      setIsSaving(true);
      const port = parseInt(createForm.port, 10);
      await apiService.createPrinter({
        name: createForm.name.trim(),
        ip: createForm.ip.trim(),
        port: Number.isFinite(port) ? port : 9100,
      });
      setSuccess(
        t('printerAdmin.createSuccess', {
          defaultValue: 'Printer created',
        })
      );
      setShowCreateForm(false);
      setCreateForm(emptyForm);
      await loadPrinters();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (printer: Printer) => {
    setEditingId(printer.printerId);
    setEditForm({
      name: printer.name,
      ip: printer.ip,
      port: String(printer.port),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
  };

  const handleSaveEdit = async (printerId: string) => {
    setError(null);
    setSuccess(null);
    try {
      setIsSaving(true);
      const port = parseInt(editForm.port, 10);
      await apiService.updatePrinter(printerId, {
        name: editForm.name.trim(),
        ip: editForm.ip.trim(),
        port: Number.isFinite(port) ? port : 9100,
      });
      setSuccess(
        t('printerAdmin.updateSuccess', {
          defaultValue: 'Printer updated',
        })
      );
      setEditingId(null);
      await loadPrinters();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleForceDelete = async (printer: Printer) => {
    if (printer.enabled) {
      setError(
        t('printerAdmin.deleteRequiresDisabled', {
          defaultValue: 'Disable the printer before deleting it.',
        })
      );
      return;
    }
    const confirmed = window.confirm(
      t('printerAdmin.deleteConfirm', {
        defaultValue: 'Permanently delete {{name}}? This cannot be undone.',
        name: printer.name,
      })
    );
    if (!confirmed) return;
    setError(null);
    setSuccess(null);
    try {
      await apiService.forceDeletePrinter(printer.printerId);
      setSuccess(
        t('printerAdmin.deleteSuccess', {
          defaultValue: 'Printer deleted',
        })
      );
      await loadPrinters();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleToggleEnabled = async (printer: Printer) => {
    setError(null);
    setSuccess(null);
    try {
      if (printer.enabled) {
        await apiService.disablePrinter(printer.printerId);
        setSuccess(
          t('printerAdmin.disableSuccess', {
            defaultValue: 'Printer disabled',
          })
        );
      } else {
        await apiService.updatePrinter(printer.printerId, { enabled: true });
        setSuccess(
          t('printerAdmin.enableSuccess', {
            defaultValue: 'Printer enabled',
          })
        );
      }
      await loadPrinters();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Breadcrumb
            currentPage={t('printerAdmin.title', {
              defaultValue: 'Printer Admin',
            })}
          />
        </div>

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('printerAdmin.title', { defaultValue: 'Printer Admin' })}
            </h1>
            <p className="mt-2 text-gray-600">
              {t('printerAdmin.description', {
                defaultValue:
                  'Manage label printers available across UM Tools.',
              })}
            </p>
          </div>
          <Button
            onClick={() => {
              setShowCreateForm((v) => !v);
              setCreateForm(emptyForm);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('printerAdmin.add', { defaultValue: 'Add printer' })}
          </Button>
        </div>

        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        <SuccessBanner message={success} onDismiss={() => setSuccess(null)} />

        {showCreateForm && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('printerAdmin.addTitle', {
                defaultValue: 'Add a new printer',
              })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                label={t('printerAdmin.fields.name', {
                  defaultValue: 'Name',
                })}
                value={createForm.name}
                onChange={(v) => setCreateForm({ ...createForm, name: v })}
                placeholder="e.g. Main Office"
              />
              <FormField
                label={t('printerAdmin.fields.ip', {
                  defaultValue: 'IP address',
                })}
                value={createForm.ip}
                onChange={(v) => setCreateForm({ ...createForm, ip: v })}
                placeholder="192.168.0.36"
              />
              <FormField
                label={t('printerAdmin.fields.port', {
                  defaultValue: 'Port',
                })}
                value={createForm.port}
                onChange={(v) => setCreateForm({ ...createForm, port: v })}
                placeholder="9100"
              />
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(false)}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  isSaving || !createForm.name.trim() || !createForm.ip.trim()
                }
                className="gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className={HEADER_CELL_CLASS}>
                    {t('printerAdmin.fields.name', { defaultValue: 'Name' })}
                  </th>
                  <th className={HEADER_CELL_CLASS}>
                    {t('printerAdmin.fields.ip', { defaultValue: 'IP' })}
                  </th>
                  <th className={HEADER_CELL_CLASS}>
                    {t('printerAdmin.fields.port', { defaultValue: 'Port' })}
                  </th>
                  <th className={HEADER_CELL_CLASS}>
                    {t('printerAdmin.fields.status', {
                      defaultValue: 'Status',
                    })}
                  </th>
                  <th className={`${HEADER_CELL_CLASS} text-right`}>
                    {t('printerAdmin.fields.actions', {
                      defaultValue: 'Actions',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {t('common.loading', { defaultValue: 'Loading...' })}
                    </td>
                  </tr>
                ) : printers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {t('printerAdmin.empty', {
                        defaultValue: 'No printers yet. Add one above.',
                      })}
                    </td>
                  </tr>
                ) : (
                  printers.map((printer) => {
                    const isEditing = editingId === printer.printerId;
                    return (
                      <tr
                        key={printer.printerId}
                        className="hover:bg-gray-50 transition"
                      >
                        <td className={BODY_CELL_CLASS}>
                          {isEditing ? (
                            <CellInput
                              value={editForm.name}
                              onChange={(v) =>
                                setEditForm({ ...editForm, name: v })
                              }
                            />
                          ) : (
                            printer.name
                          )}
                        </td>
                        <td className={BODY_CELL_CLASS}>
                          {isEditing ? (
                            <CellInput
                              value={editForm.ip}
                              onChange={(v) =>
                                setEditForm({ ...editForm, ip: v })
                              }
                            />
                          ) : (
                            printer.ip
                          )}
                        </td>
                        <td className={BODY_CELL_CLASS}>
                          {isEditing ? (
                            <CellInput
                              value={editForm.port}
                              onChange={(v) =>
                                setEditForm({ ...editForm, port: v })
                              }
                            />
                          ) : (
                            printer.port
                          )}
                        </td>
                        <td className={BODY_CELL_CLASS}>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              printer.enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {printer.enabled
                              ? t('printerAdmin.enabled', {
                                  defaultValue: 'Enabled',
                                })
                              : t('printerAdmin.disabled', {
                                  defaultValue: 'Disabled',
                                })}
                          </span>
                        </td>
                        <td className={`${BODY_CELL_CLASS} text-right`}>
                          <div className="inline-flex gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleSaveEdit(printer.printerId)
                                  }
                                  disabled={isSaving}
                                  className="gap-1"
                                >
                                  <Check className="w-3 h-3" />
                                  {t('common.save', { defaultValue: 'Save' })}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEdit}
                                  className="gap-1"
                                >
                                  <X className="w-3 h-3" />
                                  {t('common.cancel', {
                                    defaultValue: 'Cancel',
                                  })}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEdit(printer)}
                                  className="gap-1"
                                >
                                  <Edit className="w-3 h-3" />
                                  {t('common.edit', { defaultValue: 'Edit' })}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleEnabled(printer)}
                                  className="gap-1"
                                >
                                  {printer.enabled ? (
                                    <>
                                      <PowerOff className="w-3 h-3" />
                                      {t('printerAdmin.disable', {
                                        defaultValue: 'Disable',
                                      })}
                                    </>
                                  ) : (
                                    <>
                                      <Power className="w-3 h-3" />
                                      {t('printerAdmin.enable', {
                                        defaultValue: 'Enable',
                                      })}
                                    </>
                                  )}
                                </Button>
                                {/* Hard delete is only offered after a printer
                                    is already disabled — matches the API
                                    guard rail and forces a two-step opt-in. */}
                                {!printer.enabled && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleForceDelete(printer)}
                                    className="gap-1 text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    {t('printerAdmin.delete', {
                                      defaultValue: 'Delete',
                                    })}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const FormField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
    />
  </div>
);

const CellInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => (
  <input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
  />
);
